import { ArrowUpRight, FlaskConical, Lock, Ruler, Scissors } from "lucide-react";
import { useLang } from "@/i18n";
import { useDiscovery } from "@/components/Layout";
import { MaskedLines, Reveal, FadeIn, ParallaxImage, Kicker } from "@/components/motion";

const ANALYTICS =
  "https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg?auto=compress&cs=tinysrgb&w=1600";

const principleIcons = [FlaskConical, Lock, Ruler, Scissors];

export default function Approach() {
  const { t } = useLang();
  const open = useDiscovery();
  const a = t.approach;

  return (
    <>
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20 grain" data-testid="approach-hero">
        <div className="pointer-events-none absolute -left-40 top-10 h-[420px] w-[420px] rounded-full bg-gold-light/25 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn delay={0.1}>
            <Kicker>{a.kicker}</Kicker>
          </FadeIn>
          <MaskedLines
            className="mt-9 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
            delay={0.25}
            lines={[a.title]}
          />
          <Reveal delay={0.6}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-navy-900/70 sm:text-lg" data-testid="approach-intro">
              {a.intro}
            </p>
          </Reveal>
          <Reveal delay={0.75} className="mt-12">
            <ParallaxImage
              src={ANALYTICS}
              alt="Laptop con dashboard di analisi dati"
              testId="approach-hero-image"
              className="aspect-[21/9] w-full"
              speed={0.12}
            />
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="approach-phases">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          {a.phases.map((p, i) => (
            <Reveal key={i} className="group grid items-start gap-6 border-t border-navy-900/10 py-12 sm:grid-cols-12 sm:py-14">
              <div className="sm:col-span-2">
                <span className="font-serif text-5xl text-navy-900/15 transition-colors duration-500 group-hover:text-gold sm:text-6xl" data-testid={`approach-phase-num-${i}`}>
                  {p.n}
                </span>
              </div>
              <div className="sm:col-span-7">
                <h2 className="font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl" data-testid={`approach-phase-title-${i}`}>
                  {p.title}
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-navy-900/70">{p.body}</p>
              </div>
              <div className="sm:col-span-3 sm:pt-2">
                <p className="inline-block border border-gold/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-gold-deep" data-testid={`approach-phase-tag-${i}`}>
                  {p.tag}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-sand py-20 sm:py-24" data-testid="approach-principles">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Kicker>{a.principles.kicker}</Kicker>
            <h2 className="mt-4 font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">{a.principles.title}</h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-navy-900/10 bg-navy-900/10 sm:grid-cols-2 lg:grid-cols-4">
            {a.principles.items.map((p, i) => {
              const Icon = principleIcons[i];
              return (
                <Reveal key={i} delay={i * 0.08} className="group bg-sand p-8 transition-colors duration-500 hover:bg-white">
                  <Icon size={22} className="text-sage-700 transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="mt-5 text-lg font-medium tracking-tight text-navy-900" data-testid={`principle-title-${i}`}>{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy-900/60">{p.text}</p>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.25} className="mt-12 text-center">
            <button
              onClick={open}
              data-testid="approach-cta-btn"
              className="group inline-flex items-center gap-2.5 bg-navy-900 px-8 py-4 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-sage-700"
            >
              {a.cta}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
